/**
 * PIPELINE FORM — creazione e modifica di una pipeline con i suoi stati.
 *
 * Pattern di riferimento: `QuotationForm.tsx` (dual-mode new/edit su route),
 * ridotto: niente sotto-form plugin, niente relazioni, niente contesti sorgente.
 *
 * ## Editor stati "on the fly" + salvataggio aggregato
 *
 * Gli stati si definiscono/modificano/cancellano nello stesso form; al submit si
 * invia l'INTERO array (`PipelineStateInput[]`) e il backend riconcilia. La
 * `position` non si invia: la deriva l'ordine dell'array, che l'utente controlla
 * col drag&drop (@dnd-kit).
 *
 * ## Un solo iniziale, un solo finale
 *
 * La UI fa da guardia preventiva (selezione esclusiva: marcare "iniziale" su una
 * riga azzera le altre; idem "finale") e il submit ri-valida. La fonte di verità
 * resta comunque `PipelineLocalService.validate` lato backend.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useDndSensors, dndTransformStyle } from '../../components/dnd';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useToast } from '../../components/useToast';
import { ChevronDownGlyph } from '../../components/icons';
import { fetchPipeline, createPipeline, updatePipeline } from './pipelineThunks';
import { pipelineCapableTypes } from './pipelineEntityTypes';
import { entityTypeLabel } from '../../entityRegistry/entityRegistry';
import { useEnabledEntityTypes } from '../../plugin/useEnabledEntityTypes';
import { clearCurrentPipeline } from './pipelineSlice';
import type { PipelineSliceState, PipelineStateInput, PipelineStateKind } from './pipelineTypes';
import { KIND_TO_INT } from './pipelineMappers';

/** Riga dell'editor: `rowId` è un id client stabile per React e per il DnD,
 *  indipendente dal `pipelineStateId` (assente per gli stati nuovi). */
interface StateRow {
  rowId: string;
  pipelineStateId?: number;
  code: string;
  description: string;
  kind: PipelineStateKind;
}

let _rowSeq = 0;
function newRowId(): string {
  _rowSeq += 1;
  return `row-${_rowSeq}`;
}

function emptyRow(): StateRow {
  return { rowId: newRowId(), code: '', description: '', kind: 'normal' };
}

export default function PipelineForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();
  const isEdit = Boolean(id);
  const { currentItem } = useAppSelector((state) => state.pipelines as PipelineSliceState);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [className, setClassName] = useState('');
  const [rows, setRows] = useState<StateRow[]>([emptyRow()]);

  // I tipi che sanno disegnarsi in bacheca. Offrirne uno senza `board` porterebbe a un funnel la
  // cui bacheca non si apre: meglio non proporre una scelta che finisce in un vicolo cieco.
  const entityTypes = pipelineCapableTypes(useEnabledEntityTypes());

  // Un solo tipo disponibile: si preseleziona invece di far scegliere fra uno. La tendina resta
  // visibile, perche' dice comunque che tipo di funnel si sta creando.
  useEffect(() => {
    if (!isEdit && !className && entityTypes.length === 1) setClassName(entityTypes[0].fqn);
  }, [isEdit, className, entityTypes]);

  const sensors = useDndSensors();

  useEffect(() => {
    if (isEdit && id) dispatch(fetchPipeline(Number(id)));
    return () => { dispatch(clearCurrentPipeline()); };
  }, [isEdit, id, dispatch]);

  useEffect(() => {
    if (isEdit && currentItem) {
      setName(currentItem.name);
      setDescription(currentItem.description ?? '');
      setClassName(currentItem.className);
      setRows(
        currentItem.states.map((s) => ({
          rowId: newRowId(),
          pipelineStateId: s.pipelineStateId,
          code: s.code,
          description: s.description ?? '',
          kind: s.kind,
        })),
      );
    }
  }, [isEdit, currentItem]);

  function updateRow(rowId: string, patch: Partial<StateRow>) {
    setRows((prev) =>
      prev.map((r) => {
        if (r.rowId === rowId) return { ...r, ...patch };
        // Esclusività: un solo stato 'iniziale' per pipeline. Se questa riga diventa iniziale,
        // le altre iniziali tornano 'normale'. 'vinto'/'perso' sono liberi (N ciascuno), quindi
        // per gli altri patch le righe restano invariate (niente re-render inutili sul typing).
        if (patch.kind === 'initial' && r.kind === 'initial') return { ...r, kind: 'normal' };
        return r;
      }),
    );
  }

  function addRow() {
    setRows((prev) => [...prev, emptyRow()]);
  }

  function removeRow(rowId: string) {
    setRows((prev) => prev.filter((r) => r.rowId !== rowId));
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRows((prev) => {
      const oldIndex = prev.findIndex((r) => r.rowId === active.id);
      const newIndex = prev.findIndex((r) => r.rowId === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }

  /** Validazione client, specchio di PipelineLocalService.validate. */
  function validate(): string | null {
    if (!name.trim()) return t('pipeline.form.nameRequired');
    if (rows.length === 0) return t('pipeline.form.statesRequired');

    const codes = new Set<string>();
    for (const r of rows) {
      const code = r.code.trim();
      if (!code) return t('pipeline.form.stateCodeRequired');
      if (codes.has(code.toLowerCase())) return t('pipeline.form.duplicateStateCode', { code });
      codes.add(code.toLowerCase());
    }
    // 1 e un solo stato iniziale; almeno uno stato finale (vinto o perso).
    if (!isEdit && !className) return t('pipeline.form.entityTypeRequired');
    if (rows.filter((r) => r.kind === 'initial').length !== 1) return t('pipeline.form.oneInitialRequired');
    if (!rows.some((r) => r.kind === 'won' || r.kind === 'lost'))
      return t('pipeline.form.atLeastOneFinalRequired');
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const error = validate();
    if (error) {
      toast({ severity: 'error', message: error });
      return;
    }

    // L'ordine dell'array = la posizione: il backend la deriva, non la si invia.
    const states: PipelineStateInput[] = rows.map((r) => ({
      pipelineStateId: r.pipelineStateId,
      code: r.code.trim(),
      description: r.description.trim() || null,
      kind: KIND_TO_INT[r.kind],
    }));

    try {
      if (isEdit && id) {
        await dispatch(updatePipeline({
          pipelineId: Number(id),
          name: name.trim(),
          description: description.trim() || null,
          states,
        })).unwrap();
      } else {
        await dispatch(createPipeline({
          name: name.trim(),
          description: description.trim() || null,
          className,
          states,
        })).unwrap();
      }
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      return;
    }

    toast({ severity: 'success', message: t('common.saved') });
    navigate('/settings/pipelines');
  }

  return (
    // Stessa impaginazione del form preventivo: link di uscita sopra
    // l'intestazione, poi i blocchi su carta e le azioni in fondo a destra.
    <>
      <Link to="/settings/pipelines" className="tpl-back">
        <ChevronDownGlyph />
        {t('pipeline.form.back')}
      </Link>
      <div className="page-head">
        <div>
          <h1 className="page-title">{isEdit ? t('pipeline.editPipeline') : t('pipeline.createPipeline')}</h1>
        </div>
      </div>

      <form className="qform" onSubmit={handleSubmit}>
        <section className="blk">
          <div className="blk__head">
            <h2 className="blk__title">{t('pipeline.form.basicInfo')}</h2>
          </div>
          <div className="blk__body">
            {/* Il tipo sta PRIMA del nome: e' la scelta che vincola tutto il resto, e in modifica
                e' anche l'unico campo che non si puo' piu' toccare. */}
            <TextField
              select
              required
              label={t('pipeline.form.entityType')}
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              disabled={isEdit}
              helperText={isEdit ? t('pipeline.form.entityTypeLocked') : undefined}
              fullWidth
              className="form-field"
            >
              {entityTypes.map((et) => (
                <MenuItem key={et.fqn} value={et.fqn}>
                  {entityTypeLabel(et, t, et.typeName)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              label={t('pipeline.form.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              className="form-field"
            />
            <TextField
              multiline rows={2}
              label={t('pipeline.form.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              className="form-field"
            />
          </div>
        </section>

        <section className="blk">
          <div className="blk__head">
            <h2 className="blk__title">{t('pipeline.form.states')}</h2>
          </div>
          <p className="blk__hint">{t('pipeline.form.dragHint')}</p>
          <div className="blk__body">
            {rows.length === 0 && (
              <p className="text-muted">{t('pipeline.form.noStates')}</p>
            )}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={rows.map((r) => r.rowId)} strategy={verticalListSortingStrategy}>
                {rows.map((row) => (
                  <SortableStateRow
                    key={row.rowId}
                    row={row}
                    t={t}
                    onChange={(patch) => updateRow(row.rowId, patch)}
                    onRemove={() => removeRow(row.rowId)}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {/* Aggiungi stato: sotto l'ultima riga, allineato a destra. */}
            <div className="flex justify-end">
              <Button type="button" variant="outlined" size="small" onClick={addRow}>
                {t('pipeline.form.addState')}
              </Button>
            </div>
          </div>
        </section>

        {/* Azioni a destra: prima l'uscita, poi l'azione conclusiva. */}
        <div className="qform__bar">
          <Button type="button" variant="outlined" onClick={() => navigate('/settings/pipelines')}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained">
            {isEdit ? t('common.save') : t('pipeline.createPipeline')}
          </Button>
        </div>
      </form>
    </>
  );
}

function SortableStateRow({
  row,
  t,
  onChange,
  onRemove,
}: {
  row: StateRow;
  t: (key: string, opts?: Record<string, unknown>) => string;
  onChange: (patch: Partial<StateRow>) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.rowId,
  });

  // transform/transition = posizionamento dinamico del DnD (unico caso inline);
  // helper condiviso con la KanbanBoard (vedi components/dnd.ts).
  const style = dndTransformStyle(transform, transition, isDragging);

  return (
    <div ref={setNodeRef} style={style} className="pipeline-state-row flex items-center gap-sm">
      <IconButton
        type="button"
        size="small"
        className="pipeline-state-drag"
        aria-label={t('pipeline.form.dragHandle')}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>

      <TextField
        label={t('pipeline.form.stateCode')}
        value={row.code}
        onChange={(e) => onChange({ code: e.target.value })}
        size="small"
        className="pipeline-state-code"
      />
      <TextField
        label={t('pipeline.form.stateDescription')}
        value={row.description}
        onChange={(e) => onChange({ description: e.target.value })}
        size="small"
        fullWidth
      />

      {/* Caratteristica dello stato: 1 radio "iniziale" (uno solo per pipeline: `name` condiviso
          + esclusività in updateRow) e 2 checkbox "finale vinto"/"finale perso". Sono mutuamente
          esclusivi perché mappano l'unico `kind`; nessuno selezionato = stato normale. */}
      <FormControlLabel
        control={
          <Radio
            name="pipeline-initial"
            checked={row.kind === 'initial'}
            onChange={() => onChange({ kind: 'initial' })}
          />
        }
        label={t('pipeline.form.initial')}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={row.kind === 'won'}
            onChange={(e) => onChange({ kind: e.target.checked ? 'won' : 'normal' })}
          />
        }
        label={t('pipeline.form.finalWon')}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={row.kind === 'lost'}
            onChange={(e) => onChange({ kind: e.target.checked ? 'lost' : 'normal' })}
          />
        }
        label={t('pipeline.form.finalLost')}
      />

      <IconButton
        type="button"
        size="small"
        className="pipeline-state-remove"
        onClick={onRemove}
        aria-label={t('pipeline.form.removeState')}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </div>
  );
}
